import { ListItem } from '@ninjacart/nc-nk-adaptive-cards';
import {
  useEventZStore,
  UserProps,
} from '@ninjacart/nc-nk-store';
import {
  HomePlaceHolder,
  Separator,
  Spinner,
  View
} from '@ninjacart/nc-nk-ui';
import { BannerAds, useIsFocused, useScreenDimensions } from '@ninjacart/nc-nk-web-deps';
import { FlashList } from '@shopify/flash-list';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions } from 'react-native';
import useUserEngagementStore from '../store/useUserEngagementStore';
import {ProfileTabEmptyState} from './profileTabEmptyState';

type VideosParams = {
  userProfile: UserProps;
  id?: string;
  hasUserLoggedIn: boolean | null;
  onReportBottomSheetOpen: Function;
};

const AllVideos = ({ userProfile, id, hasUserLoggedIn, onReportBottomSheetOpen }: VideosParams) => {
  const { t } = useTranslation();
  const startCountVideoRef = useRef({ start: 0, count: 10 });
  const [refreshingVideo, setRefreshingVideo] = useState(false);
  const [onEndReachedSpinnerVideo, setOnEndReachedSpinnerVideo] = useState(false);
  const screenDimensions = useScreenDimensions();
  const homeFeedsCard = useUserEngagementStore((state) => state.homeFeedsCard);
  const isVideoLoaded = useUserEngagementStore((state) => state.isVideoLoaded);
  const fetchAllVideosData = useUserEngagementStore((state) => state.fetchAllVideosData);
  const resetHomeFeedsCard = useUserEngagementStore((state) => state.resetHomeFeedsCard);
  const isFocused = useIsFocused();
  const userId = id ?? userProfile?.user?.id!;
  const deviceWidth = Dimensions.get('window').width;
  const bannerAdKeywords = useEventZStore((state) => state.bannerAdKeywords);

  const videoListPayloadRef = useRef({
    userId: userId,
    offset: startCountVideoRef.current.start,
    count: startCountVideoRef.current.count,
  });

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    if (isFocused) {
      videoListPayloadRef.current.offset = 0;
      resetHomeFeedsCard();
      fetchAllVideosData(videoListPayloadRef?.current, false);
    }
  }, [isFocused]);

  const onPullToRefreshVideo = async () => {
    setRefreshingVideo(true);
    startCountVideoRef.current.start = 0;
    resetHomeFeedsCard();
    videoListPayloadRef.current.offset = 0;
    try {
      await fetchAllVideosData(videoListPayloadRef.current, true);
    } catch (err) {
      console.log('err video list onPullToRefreshVideo', err);
    } finally {
      setRefreshingVideo(false);
    }
  };

  const onEndReachedVideo = async () => {
    startCountVideoRef.current.start += startCountVideoRef.current.count;
    videoListPayloadRef.current.offset = startCountVideoRef.current.start;
    setOnEndReachedSpinnerVideo(true);
    try {
      await fetchAllVideosData(videoListPayloadRef.current, false);
    } catch (err) {
      console.log('err video list onEndReachedVideo', err);
    } finally {
      setOnEndReachedSpinnerVideo(false);
    }
  };

  const renderVideoList = ({ item, index }) => {
    switch (item?.type) {
      case 'BANNER_AD':
        return (
          <BannerAds
            deviceWidth={deviceWidth}
            itemKey={item?.actionData?.content?.itemKey ?? index.toString()}
            unitId={item?.actionData?.content?.unitId}
            adSizeDefs={item?.actionData?.content?.adSizeDefs ?? []}
            bannerAdKeywords={bannerAdKeywords}
            screenName={'profile all video page'}
          />
        );
      default:
        return (
          <ListItem
            item={item}
            index={index}
            userProfile={userProfile}
            screenDimensions={screenDimensions}
            onReportBottomSheetOpen={onReportBottomSheetOpen}
            onCommentBottomSheetOpen={() => { } }
            pageName={'Profile Detail'} isAutoPlayBannerAdEnabled={false}          />
        )
    }

  };

  const videoListEmptyComponent = () =>
    isVideoLoaded &&
    !homeFeedsCard?.length && (
      <ProfileTabEmptyState emptyStateContent={t('page.youHavenotUploadAnyVideos')} />
    );

  if (!isVideoLoaded) return <HomePlaceHolder />;

  return (
    <View flex={1} backgroundColor={'$background'}>
      <FlashList
        data={homeFeedsCard}
        renderItem={renderVideoList}
        getItemType={(item) => item.type}
        keyExtractor={(item, index) => index.toString()}
        scrollEventThrottle={24}
        estimatedItemSize={430}
        showsVerticalScrollIndicator={false}
        onEndReachedThreshold={0.8}
        onEndReached={onEndReachedVideo}
        refreshing={refreshingVideo}
        onRefresh={onPullToRefreshVideo}
        ItemSeparatorComponent={Separator}
        ListFooterComponent={() =>
          onEndReachedSpinnerVideo && (
            <View style={{ flex: 1, padding: 10, alignItems: 'center' }}>
              <Spinner size="large" color="$green10" />
            </View>
          )
        }
        ListFooterComponentStyle={{ height: 100 }}
        ListEmptyComponent={videoListEmptyComponent}
      />
    </View>
  );
};

export default AllVideos;
